import { useCallback, useMemo } from 'react';

import { useGetVideo } from '@vimeo/gctl-api/hooks/useVideo';
import {
  buildActionBpContext,
  buildViewBpContext,
  buildProductAnalyticsBpContext,
  buildTeamBpContextFromTeamUser,
  buildVideoBpContext,
  buildWebBpContext,
  sendBpEventWithContexts,
  buildThirdPartyIntegrationBpContext,
} from '@vimeo/use-tracking';
import { useViewer } from '@vimeo/viewer';

import * as finalCopy from './copy/final';
import { useContainerDataStore } from './stores/container-data-store';
import type { FeatureType } from './types';
import { getDeviceType } from './utils';

type TranslationCopyType = 'subtitles_only' | 'audio_and_subtitles';
type TranslationFlowType = 'subtitles' | 'audio_and_subtitles';

const upsellDefaults = {
  add_on_feature: null,
  currency: null,
  discount_offer: null,
  duration: null,
  interface_type: null,
  is_discount: null,
  promo_code_id: null,
  purchase_type: null,
  selected_plan: null,
  usd_price: null,
};

/**
 * A hook for generating the relevant BP events for AI Action Menu components that live on SVV Manage.
 * Populates the relevant contexts in one spot.
 */
export const useGetSvvManageBpEvents = () => {
  const viewer = useViewer();
  const videoId = useContainerDataStore((state) => state.videoId);
  const pageName = useContainerDataStore((state) => state.pageName);

  const defaultSvvLocationValue = 'sidebar';

  const { data: videoData } = useGetVideo({
    where: { videoId },
    select: ['duration', 'uploader.link', 'user.uri'],
  });

  const duration = videoData?.duration ?? null;
  const teamUser = viewer?.teamUser;
  const isSVVManage = pageName === 'single_video_view_manage';
  const isPlayer = pageName === 'player';

  const isUploader = useMemo(() => {
    if (viewer?.user?.id && videoData?.uploader?.link) {
      // Getting useId from user link
      const creatorId = videoData.uploader?.link.split('/user')[1];

      return creatorId ? parseInt(creatorId, 10) === viewer.user?.id : false;
    }

    return false;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [viewer?.user?.id, videoData?.uploader?.link]);

  const clipOwnerId = useMemo(
    () => parseInt(videoData?.user?.uri.split('/users/')[1] || '', 10) || null,
    [videoData?.user?.uri],
  );

  const buildTeamContext = useCallback(() => buildTeamBpContextFromTeamUser(teamUser), [teamUser]);

  const buildVideoContext = useCallback(
    () => buildVideoBpContext({ video_id: videoId, video_owner_id: clipOwnerId, duration }),
    [clipOwnerId, duration, videoId],
  );

  const buildViewContext = useCallback(
    () => buildViewBpContext({ view_type: 'impression', feature: null }),
    [],
  );

  const buildActionContext = useCallback(
    (type: 'click' | 'hover' | 'type') =>
      buildActionBpContext({ action_type: type, feature: null }),
    [],
  );

  const buildProductAnalyticsContext = useCallback(
    (
      feature: FeatureType,
      copy: string | null,
      flow: 'ai' | 'share_highlight_video_link' | 'subtitles' | 'audio_and_subtitles' | null = null,
      element: 'button' | 'check_box' | 'list' | 'toggle' | 'radio_button' | null = null,
      location: 'drawer' | 'modal' | 'sidebar' | 'player' | 'viewer_home' | null = null,
    ) =>
      buildProductAnalyticsBpContext({
        product: 'ai',
        feature,
        copy,
        device_type: getDeviceType(),
        location: location ?? (isSVVManage ? defaultSvvLocationValue : pageName),
        modal_name: null,
        element: element || (isSVVManage ? 'button' : null),
        flow,
      }),
    [isSVVManage, pageName, defaultSvvLocationValue],
  );

  const buildWebContext = useCallback(
    () =>
      buildWebBpContext({
        path: window.location.pathname,
        page_name: isPlayer ? 'embedded_page' : pageName,
        location: null,
        referrer_page_name: null,
      }),
    [pageName, isPlayer],
  );

  // Well, isn't this the silliest thing that you ever did see?
  const buildThirdPartyIntegrationContext = useCallback(
    () =>
      buildThirdPartyIntegrationBpContext({
        integration_id: null,
        integration_name: null,
        is_partner: null,
      }),
    [],
  );

  const sendSparkleButtonClickEvent = () => {
    sendBpEventWithContexts(
      'vimeo.open_ai_options',
      {
        ...buildActionContext('click'),
        ...buildProductAnalyticsContext('ai', 'ask_ai'),
        ...buildWebContext(),
        ...buildVideoContext(),
        ...buildTeamContext(),
      },
      1,
      {
        value: 'svv_redesign',
        device_type: null,
      },
    );
  };

  const sendSparkleButtonHoverEvent = () => {
    sendBpEventWithContexts(
      'vimeo.hover_on_ai_options',
      {
        ...buildActionContext('hover'),
        ...buildProductAnalyticsContext('ai', 'ask_ai'),
        ...buildVideoContext(),
        ...buildWebContext(),
        ...buildTeamContext(),
      },
      1,
      {
        value: 'svv_redesign',
        device_type: null,
      },
    );
  };

  const sendGenerateVideoDetailsLoaderViewedEvent = (copy?: string | null) => {
    sendBpEventWithContexts(
      'vimeo.view_generate_video_details',
      {
        ...buildViewContext(),
        ...buildProductAnalyticsContext('ai', copy ?? 'ask_ai'),
        ...buildWebContext(),
        ...buildVideoContext(),
        ...buildTeamContext(),
      },
      1,
      {
        value: null,
        service_version: null,
      },
    );
  };

  const sendGenerateHighlightsLoaderViewedEvent = () => {
    sendBpEventWithContexts(
      'vimeo.view_generate_highlights',
      {
        ...buildViewContext(),
        ...buildProductAnalyticsContext('highlights', finalCopy.generatingHighlights),
        ...buildWebContext(),
        ...buildVideoContext(),
        ...buildTeamContext(),
      },
      1,
      {
        value: null,
        service_version: null,
      },
    );
  };

  const sendViewGenerateChaptersEvent = (copy?: string | null) => {
    sendBpEventWithContexts(
      'vimeo.view_generate_chapters',
      {
        ...buildViewContext(),
        ...buildProductAnalyticsContext('ai', copy ?? 'ask_ai'),
        ...buildWebContext(),
        ...buildVideoContext(),
        ...buildTeamContext(),
      },
      1,
      {
        value: null,
        service_version: null,
      },
    );
  };

  const sendViewGenerateAskAIEvent = () => {
    sendBpEventWithContexts(
      'vimeo.view_generate_ask_ai',
      {
        ...buildViewContext(),
        ...buildProductAnalyticsContext('ai', 'ask_ai'),
        ...buildWebContext(),
        ...buildVideoContext(),
        ...buildTeamContext(),
      },
      1,
      {
        value: null,
        service_version: null,
      },
    );
  };

  const sendSummaryDetailsSaveClickEvent = (
    copy: 'ask_ai' | 'description' | 'replace' | 'tags' | 'title',
  ) => {
    sendBpEventWithContexts(
      'vimeo.save_video_details',
      {
        ...buildProductAnalyticsContext('video_details', copy),
        ...buildActionContext('click'),
        ...buildVideoContext(),
        ...buildWebContext(),
        ...buildTeamContext(),
      },
      1,
      { value: null, device_type: null },
    );
  };

  const sendChapterDetailsSaveClickEvent = (copy: string) => {
    sendBpEventWithContexts(
      'vimeo.save_video_chapters',
      {
        ...buildActionContext('click'),
        ...buildProductAnalyticsContext('chapters', copy),
        ...buildWebContext(),
        ...buildVideoContext(),
        ...buildTeamContext(),
      },
      2,
      { value: null },
    );
  };

  const sendSummaryDetailsViewedEvent = (copy?: string | null) => {
    sendBpEventWithContexts(
      'vimeo.view_suggested_video_details',
      {
        ...buildViewContext(),
        ...buildProductAnalyticsContext('ai', copy ?? 'ask_ai'),
        ...buildWebContext(),
        ...buildVideoContext(),
        ...buildTeamContext(),
      },
      1,
      {
        value: null,
        service_version: null,
      },
    );
  };

  const sendChapterDetailsViewedEvent = (copy?: string | null) => {
    sendBpEventWithContexts(
      'vimeo.view_suggested_chapters',
      {
        ...buildViewContext(),
        ...buildProductAnalyticsContext('ai', copy ?? 'ask_ai'),
        ...buildWebContext(),
        ...buildVideoContext(),
        ...buildTeamContext(),
      },
      1,
      {
        value: null,
        service_version: null,
      },
    );
  };

  const sendSummaryNotificationViewedEvent = (
    status: 'generate_video_summary_failed' | 'generate_video_summary_success',
    notificationCopy: string | null,
    errorCode: string | null,
    errorName: string | null,
  ) => {
    sendBpEventWithContexts(
      'vimeo.notification_view',
      {
        ...buildProductAnalyticsContext('ai', notificationCopy),
        ...buildViewContext(),
        ...buildWebContext(),
        ...buildVideoContext(),
        ...buildTeamContext(),
      },
      3,
      {
        notification_name: status,
        error_id: errorCode ?? null,
        error_name: errorName ?? null,
        checkbox_copy: null,
        notification_copy: null,
      },
    );
  };

  const sendThumbsOnVideoTitleClickEvent = (
    ratedFeature: string,
    thumbsUpState: boolean,
    thumbsDownState: boolean,
  ) => {
    sendBpEventWithContexts(
      'vimeo.thumbs_rate',
      {
        ...buildActionContext('click'),
        ...buildProductAnalyticsContext('video_details', 'ask_ai'),
        ...buildTeamContext(),
        ...buildVideoContext(),
      },
      1,
      {
        rated_feature: ratedFeature,
        thumbs_up_state: thumbsUpState,
        thumbs_down_state: thumbsDownState,
      },
    );
  };

  const sendAskAQuestionEvent = (value: string) => {
    sendBpEventWithContexts(
      'vimeo.typing_ai_question',
      {
        ...buildActionContext('type'),
        ...buildProductAnalyticsContext('ask_ai', value, 'ai'),
        ...buildWebContext(),
        ...buildTeamContext(),
        ...buildVideoContext(),
      },
      3,
      {
        value,
        if_uploader: isUploader,
      },
    );
  };

  const sendViewGenerateAIAnswerEvent = () => {
    sendBpEventWithContexts(
      'vimeo.view_generate_ai_answer',
      {
        ...buildViewContext(),
        ...buildProductAnalyticsContext('ask_ai', finalCopy.generatingAnswer, 'ai'),
        ...buildWebContext(),
        ...buildTeamContext(),
        ...buildVideoContext(),
      },
      1,
      {
        value: null,
        service_version: null,
      },
    );
  };

  const sendViewSuggestedAIAnswerEvent = (question: string, answer: string) => {
    sendBpEventWithContexts(
      'vimeo.view_suggested_ai_answer',
      {
        ...buildViewContext(),
        ...buildProductAnalyticsContext('ask_ai', question, 'ai'),
        ...buildWebContext(),
        ...buildTeamContext(),
        ...buildVideoContext(),
      },
      4,
      {
        value: null,
        service_version: null,
        error_value: null,
        extra_text_field_1: answer,
      },
    );
  };

  const sendViewSuggestedAIHighlightVideosEvent = () => {
    sendBpEventWithContexts(
      'vimeo.view_suggested_highlight_videos',
      {
        ...buildViewContext(),
        ...buildProductAnalyticsContext('highlights', finalCopy.highlightsSectionTitle),
        ...buildWebContext(),
        ...buildVideoContext(),
        ...buildTeamContext(),
      },
      1,
      {
        value: null,
        service_version: null,
      },
    );
  };

  const sendSelectHighlightVideo = ({
    videoTitle,
    value,
  }: {
    videoTitle: string;
    value: 'recap' | 'moment';
  }) => {
    sendBpEventWithContexts(
      'vimeo.select_highlight_video',
      {
        ...buildActionContext('click'),
        ...buildProductAnalyticsContext('highlights', videoTitle),
        ...buildWebContext(),
        ...buildVideoContext(),
        ...buildTeamContext(),
      },
      1,
      {
        value,
        device_type: null,
      },
    );
  };

  const sendSaveHighlightVideo = ({ videoTitle }: { videoTitle: string }) => {
    sendBpEventWithContexts(
      'vimeo.save_highlight_video',
      {
        ...buildActionContext('click'),
        ...buildProductAnalyticsContext('highlights', videoTitle),
        ...buildWebContext(),
        ...buildVideoContext(),
        ...buildTeamContext(),
      },
      1,
      {
        value: 'svv_redesign',
        device_type: null,
      },
    );
  };

  const sendEditHighlightVideo = ({ videoTitle }: { videoTitle: string }) => {
    sendBpEventWithContexts(
      'vimeo.edit_highlight_video',
      {
        ...buildActionContext('click'),
        ...buildProductAnalyticsContext('highlights', videoTitle),
        ...buildWebContext(),
        ...buildVideoContext(),
        ...buildTeamContext(),
      },
      1,
      {
        value: 'svv_redesign',
        device_type: null,
      },
    );
  };

  const sendEmailAiSummaryRequestEvent = () => {
    sendBpEventWithContexts(
      'vimeo.email_ai_summary_request',
      {
        ...buildActionContext('click'),
        ...buildProductAnalyticsContext('video_details', null),
        ...buildWebContext(),
        ...buildVideoContext(),
        ...buildTeamContext(),
      },
      2,
      { value: null },
    );
  };

  const sendSelectSuggestedAIQuestionEvent = ({
    copy,
    isRelated,
  }: {
    copy: string;
    isRelated: boolean;
  }) => {
    sendBpEventWithContexts(
      'vimeo.select_suggested_ai_question',
      {
        ...buildActionContext('click'),
        ...buildProductAnalyticsContext('ask_ai', copy, 'ai'),
        ...buildWebContext(),
        ...buildVideoContext(),
        ...buildTeamContext(),
      },
      3,
      {
        value: isRelated ? 'related_question' : 'suggested_question',
        if_uploader: isUploader,
      },
    );
  };

  const sendViewSuggestedAskAIEvent = () => {
    sendBpEventWithContexts(
      'vimeo.view_suggested_ask_ai',
      {
        ...buildViewContext(),
        ...buildProductAnalyticsContext('ask_ai', 'generate_Q&A'),
        ...buildWebContext(),
        ...buildVideoContext(),
        ...buildTeamContext(),
      },
      1,
      {
        value: null,
        service_version: null,
      },
    );
  };

  const sendSelectGenerateVideoSummaryEvent = () => {
    sendBpEventWithContexts(
      'vimeo.select_generate_video_summary',
      {
        ...buildActionContext('click'),
        ...buildProductAnalyticsContext('ai', finalCopy.activatingAi),
        ...buildWebContext(),
        ...buildVideoContext(),
        ...buildTeamContext(),
      },
      1,
      {
        value: 'svv_redesign',
        device_type: null,
      },
    );
  };

  const sendSelectGenerateChaptersEvent = () => {
    sendBpEventWithContexts(
      'vimeo.select_generate_chapters',
      {
        ...buildActionContext('click'),
        ...buildProductAnalyticsContext('ai', finalCopy.activatingAi),
        ...buildWebContext(),
        ...buildVideoContext(),
        ...buildTeamContext(),
      },
      1,
      {
        value: 'svv_redesign',
        device_type: null,
      },
    );
  };

  const sendSelectGenerateHighlightsEvent = () => {
    sendBpEventWithContexts(
      'vimeo.select_generate_highlights',
      {
        ...buildActionContext('click'),
        ...buildProductAnalyticsContext('ai', finalCopy.activatingAi),
        ...buildWebContext(),
        ...buildVideoContext(),
        ...buildTeamContext(),
      },
      1,
      {
        value: 'svv_redesign',
        device_type: null,
      },
    );
  };

  const sendSelectGenerateAskAIEvent = () => {
    sendBpEventWithContexts(
      'vimeo.select_generate_ask_ai',
      {
        ...buildActionContext('click'),
        ...buildProductAnalyticsContext('ai', finalCopy.activatingAi),
        ...buildWebContext(),
        ...buildVideoContext(),
        ...buildTeamContext(),
      },
      1,
      {
        value: 'svv_redesign',
        device_type: null,
      },
    );
  };

  const sendFeatureButtonUpgradeClickEvent = (feature: FeatureType) => {
    sendBpEventWithContexts(
      'vimeo.trigger_upsell',
      {
        ...buildActionContext('click'),
        ...buildProductAnalyticsContext(feature, finalCopy.upgradeToAccessAI),
        ...buildWebContext(),
        ...buildVideoContext(),
        ...buildTeamContext(),
        ...buildThirdPartyIntegrationContext(),
      },
      4,
      {
        ...upsellDefaults,
        upsell_name: 'access_ai_upgrade',
      },
    );
  };

  const sendEnterpriseCTAImpressionEvent = (feature: FeatureType) => {
    sendBpEventWithContexts(
      'vimeo.upsell_trigger_impression',
      {
        ...buildProductAnalyticsContext(feature, 'upgrade'),
        ...buildWebContext(),
        ...buildVideoContext(),
        ...buildTeamContext(),
        ...buildThirdPartyIntegrationContext(),
        ...buildViewContext(),
      },
      2,
      {
        ...upsellDefaults,
        upsell_name: 'access_ai_highlights',
      },
    );
  };

  const sendEnterpriseCTAClickEvent = () => {
    sendBpEventWithContexts('vimeo.trigger_contact_sales', {
      ...buildActionContext('click'),
      ...buildProductAnalyticsContext('highlights', 'Enterprise'),
      ...buildWebContext(),
      ...buildVideoContext(),
      ...buildTeamContext(),
      ...buildThirdPartyIntegrationContext(),
    });
  };

  const sendErrorNotificationViewEvent = (copy: string, name: string) => {
    sendBpEventWithContexts(
      'vimeo.notification_view',
      {
        ...buildProductAnalyticsContext('ai', copy),
        ...buildViewContext(),
        ...buildWebContext(),
        ...buildVideoContext(),
        ...buildTeamContext(),
      },
      3,
      {
        notification_name: copy,
        error_id: null,
        error_name: name,
        checkbox_copy: null,
        notification_copy: null,
      },
    );
  };

  const sendTranslateVideoMenuClickedEvent = () => {
    sendBpEventWithContexts(
      'vimeo.select_translate_video',
      {
        ...buildProductAnalyticsContext('ai_translate', 'translate_video'),
        ...buildActionContext('click'),
        ...buildWebContext(),
        ...buildVideoContext(),
        ...buildTeamContext(),
      },
      4,
      {
        if_uploader: isUploader,
        value: null,
      },
    );
  };

  const sendPlayMomentClickEvent = (timecode: number, question: string) => {
    sendBpEventWithContexts(
      'vimeo.play_video_moment',
      {
        ...buildActionContext('click'),
        ...buildProductAnalyticsContext('ask_ai', question, 'ai', 'button'),
        ...buildWebContext(),
        ...buildVideoContext(),
        ...buildTeamContext(),
      },
      4,
      {
        if_uploader: isUploader,
        value: String(timecode),
      },
    );
  };

  const sendTranslateVideoModalViewEvent = (
    copy: 'enabled_settings' | 'disabled_settings' | TranslationCopyType,
    value: number,
    flow: TranslationFlowType,
  ) => {
    sendBpEventWithContexts(
      'vimeo.view_translate_video_settings',
      {
        ...buildProductAnalyticsContext('ai_translate', copy, flow),
        ...buildWebContext(),
        ...buildViewContext(),
        ...buildVideoContext(),
        ...buildTeamContext(),
      },
      2,
      {
        service_version: null,
        value,
      },
    );
  };

  const sendTranslateVideoLanguageSelectedEvent = (
    language: string,
    flow: TranslationFlowType,
    copy: 'translate_to' | 'translate_from',
  ) => {
    sendBpEventWithContexts(
      'vimeo.select_translate_language',
      {
        ...buildProductAnalyticsContext('ai_translate', copy, flow, 'check_box'),
        ...buildActionContext('click'),
        ...buildWebContext(),
        ...buildVideoContext(),
        ...buildTeamContext(),
      },
      4,
      {
        value: language,
        if_uploader: isUploader,
      },
    );
  };

  const sendTranslateVideoSpeakersNumberSelectedEvent = (numberOfSpeakers: string) => {
    sendBpEventWithContexts(
      'vimeo.select_translate_number_of_speakers',
      {
        ...buildProductAnalyticsContext('ai_translate', '', null, 'list'),
        ...buildActionContext('click'),
        ...buildWebContext(),
        ...buildVideoContext(),
        ...buildTeamContext(),
      },
      4,
      {
        value: numberOfSpeakers,
        if_uploader: isUploader,
      },
    );
  };

  const sendTranslateVideoToggleSwitchEvent = (copy: 'enable' | 'disable') => {
    sendBpEventWithContexts(
      'vimeo.translate_preview_toggle_switch',
      {
        ...buildProductAnalyticsContext('ai_translate', copy, null, 'toggle'),
        ...buildActionContext('click'),
        ...buildWebContext(),
        ...buildVideoContext(),
        ...buildTeamContext(),
      },
      4,
      {
        if_uploader: isUploader,
        value: null,
      },
    );
  };

  const sendTranslateSaveTranslateSettingsEvent = (
    copy: 'Continue' | 'Finish and save',
    path: 'preview' | 'full_apply',
    amount: string,
    flow: TranslationFlowType,
  ) => {
    sendBpEventWithContexts(
      'vimeo.save_translate_settings',
      {
        ...buildProductAnalyticsContext('ai_translate', copy, flow, 'toggle'),
        ...buildActionContext('click'),
        ...buildWebContext(),
        ...buildVideoContext(),
        ...buildTeamContext(),
      },
      4,
      {
        if_uploader: isUploader,
        value: path === 'preview' ? path : amount,
      },
    );
  };

  const sendTranslateGenerateTranslationEvent = (copy: 'short_translate' | 'long_translate') => {
    sendBpEventWithContexts(
      'vimeo.view_generate_translation',
      {
        ...buildProductAnalyticsContext('ai_translate', copy, null, 'button'),
        ...buildWebContext(),
        ...buildViewContext(),
        ...buildVideoContext(),
        ...buildTeamContext(),
      },
      1,
      {
        value: null,
        service_version: null,
      },
    );
  };

  const sendTranslateLanguageToDiscardEvent = (language: string) => {
    sendBpEventWithContexts(
      'vimeo.select_translate_language_to_discard',
      {
        ...buildProductAnalyticsContext(
          'ai_translate',
          null,
          null,
          'button',
          isSVVManage ? defaultSvvLocationValue : 'drawer',
        ),
        ...buildActionContext('click'),
        ...buildWebContext(),
        ...buildVideoContext(),
        ...buildTeamContext(),
      },
      1,
      { value: language, device_type: getDeviceType() },
    );
  };

  const sendCopyVideoDetailsEvent = (value: string) => {
    sendBpEventWithContexts(
      'vimeo.copy_video_details',
      {
        ...buildTeamContext(),
        ...buildVideoContext(),
        ...buildWebContext(),
        ...buildActionContext('click'),
        ...buildProductAnalyticsContext(
          'video_details',
          value,
          null,
          null,
          defaultSvvLocationValue,
        ),
      },
      4,
      {
        value,
        if_uploader: isUploader,
      },
    );
  };

  const sendDistributeContentEvent = () => {
    sendBpEventWithContexts(
      'vimeo.distribute_content',
      {
        ...buildThirdPartyIntegrationContext(),
        ...buildWebContext(),
        ...buildVideoContext(),
        ...buildViewContext(),
        ...buildActionContext('click'),
        ...buildProductAnalyticsContext(
          'highlights',
          null,
          'share_highlight_video_link',
          null,
          defaultSvvLocationValue,
        ),
      },
      9,
      {
        distribution_type: 'share',
        target_quality: null,
        target_resolution: null,
        target_file_details: null,
        number_of_items: 1,
        embed_config: null,
        embed_custom_dimensions: null,
        is_internal: false,
        sharee_id: null,
        sharee_entity_permission: null,
        is_send_email_notification: false,
        sharee_team_permission: null,
        sharee_email: null,
        collection_type: null,
      },
    );
  };

  const sendViewTranslationProductSelection = (copy: TranslationCopyType) => {
    sendBpEventWithContexts(
      'vimeo.view_translation_product_selection',
      {
        ...buildViewContext(),
        ...buildProductAnalyticsContext(
          'ai_translate',
          copy,
          null,
          'radio_button',
          defaultSvvLocationValue,
        ),
        ...buildVideoContext(),
        ...buildTeamContext(),
      },
      2,
      { service_version: null, value: null },
    );
  };

  const sendSelectTranslationProduct = (value: TranslationCopyType) => {
    sendBpEventWithContexts(
      'vimeo.select_translation_product',
      {
        ...buildVideoContext(),
        ...buildWebContext(),
        ...buildTeamContext(),
        ...buildActionContext('click'),
        ...buildProductAnalyticsContext(
          'ai_translate',
          value,
          null,
          'radio_button',
          defaultSvvLocationValue,
        ),
      },
      4,
      {
        value,
        if_uploader: isUploader,
      },
    );
  };

  return {
    sendPlayMomentClickEvent,
    sendSparkleButtonClickEvent,
    sendSparkleButtonHoverEvent,
    sendSummaryNotificationViewedEvent,
    sendGenerateHighlightsLoaderViewedEvent,
    sendGenerateVideoDetailsLoaderViewedEvent,
    sendSummaryDetailsViewedEvent,
    sendSummaryDetailsSaveClickEvent,
    sendChapterDetailsViewedEvent,
    sendChapterDetailsSaveClickEvent,
    sendThumbsOnVideoTitleClickEvent,
    sendAskAQuestionEvent,
    sendViewGenerateAIAnswerEvent,
    sendViewSuggestedAIAnswerEvent,
    sendViewSuggestedAIHighlightVideosEvent,
    sendSelectHighlightVideo,
    sendSaveHighlightVideo,
    sendEditHighlightVideo,
    sendEmailAiSummaryRequestEvent,
    sendSelectSuggestedAIQuestionEvent,
    sendViewSuggestedAskAIEvent,
    sendErrorNotificationViewEvent,

    // AI Translations
    sendTranslateVideoMenuClickedEvent,
    sendTranslateVideoToggleSwitchEvent,
    sendTranslateGenerateTranslationEvent,
    sendTranslateLanguageToDiscardEvent,
    sendTranslateVideoLanguageSelectedEvent,
    sendTranslateVideoSpeakersNumberSelectedEvent,
    sendTranslateVideoModalViewEvent,
    sendTranslateSaveTranslateSettingsEvent,
    sendCopyVideoDetailsEvent,
    sendDistributeContentEvent,
    sendViewTranslationProductSelection,
    sendSelectTranslationProduct,

    // viewing the loader after selecting Option from main menu
    sendViewGenerateChaptersEvent,
    sendViewGenerateAskAIEvent,

    // selecting Option from main menu
    sendSelectGenerateVideoSummaryEvent,
    sendSelectGenerateChaptersEvent,
    sendSelectGenerateHighlightsEvent,
    sendSelectGenerateAskAIEvent,

    // Upsells
    sendEnterpriseCTAImpressionEvent,
    sendEnterpriseCTAClickEvent,
    sendFeatureButtonUpgradeClickEvent,
  };
};
